/**
 * useNext7DaysSnapshot Hook
 * 
 * Purpose:
 * Custom hook for fetching next 7 days snapshot data (upcoming events). This fetch is independent
 * of the selected month - it always fetches the next 7 days from today, regardless of which month
 * is displayed in the calendar. Handles backend validation errors gracefully (known issue where
 * backend incorrectly validates reservation_id for listing_id-only endpoints).
 * 
 * High-level Workflow:
 * 1. Guard checks: Validates prerequisites (listingId, propertiesLoading, selectedProperty)
 *    before attempting fetch. If prerequisites not met, clears data and returns.
 * 2. API fetch: Calls getNext7DaysSnapshot with listingId only (no date range needed - always
 *    next 7 days from today).
 * 3. Response processing: Normalizes response payload (handles wrapped/flat formats). If no
 *    valid payload but existing snapshot exists, keeps existing snapshot (graceful degradation).
 * 4. Error handling: Special handling for known backend validation errors (silently falls back).
 *    Other errors show user-facing error message but keep existing snapshot.
 * 5. Cleanup: No special cleanup needed (no AbortController support yet).
 * 
 * Assumptions & Invariants:
 * - Fetch gating: All fetches gated by listingId exists, propertiesLoading is false, and
 *   selectedProperty object exists. Same guards as other fetches.
 * - Date independence: This fetch is independent of calendarMonth. Always fetches next 7 days
 *   from today, regardless of which month is displayed. This ensures upcoming events snapshot
 *   always shows current/relevant data.
 * - Response normalization: Uses normalizeApiPayload to handle wrapped/flat response formats.
 * - Graceful degradation: If API returns no valid payload but existing snapshot exists, keeps
 *   existing snapshot (doesn't clear on error). Only clears if no snapshot exists yet.
 * - Backend validation error: Known issue where backend incorrectly validates reservation_id
 *   for listing_id-only endpoints. This is handled gracefully - silently falls back (no
 *   console.error spam). Only logs warning if perf_debug flag is enabled.
 * - Error state: Sets error message on failure but doesn't clear existing snapshot (keeps last
 *   known good state). Only clears snapshot if no snapshot exists yet.
 * 
 * Dependencies:
 * - Internal:
 *   - services/ownerApi.js: getNext7DaysSnapshot API function
 *   - utils/calendarNormalization.js: normalizeApiPayload function
 * - External:
 *   - react: useState, useEffect
 * 
 * Version: 1.0.0
 * 
 * Change Log:
 * - 2026-01-26: Initial extraction from CalendarPage.jsx (Phase 2 refactoring)
 */

import { useState, useEffect, useRef } from 'react';
import { getNext7DaysSnapshot } from '../services/ownerApi.js';
import { normalizeApiPayload } from '../utils/calendarNormalization.js';

/**
 * Hook for fetching next 7 days snapshot (upcoming events)
 * 
 * @param {number|null} listingId - Property listing ID (primitive, stable)
 * @param {boolean} propertiesLoading - Whether properties are still loading
 * @param {Object|null} selectedProperty - Selected property object (for guard check)
 * @returns {Object} { next7DaysSnapshot, next7DaysSnapshotLoading, next7DaysSnapshotError }
 */
export function useNext7DaysSnapshot({
  listingId,
  propertiesLoading,
  selectedProperty,
}) {
  // State for next 7 days snapshot data
  const [next7DaysSnapshot, setNext7DaysSnapshot] = useState(null);
  const [next7DaysSnapshotLoading, setNext7DaysSnapshotLoading] = useState(false);
  const [next7DaysSnapshotError, setNext7DaysSnapshotError] = useState(null);
  
  // Ref to track current snapshot value for closure access (preserves original behavior)
  const next7DaysSnapshotRef = useRef(null);
  useEffect(() => {
    next7DaysSnapshotRef.current = next7DaysSnapshot;
  }, [next7DaysSnapshot]);

  // Fetch next 7 days snapshot (independent of selected month)
  useEffect(() => {
    const perfDebug = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('perf_debug') === '1';

    // CRITICAL FIX: Also check propertiesLoading AND selectedProperty to prevent race condition
    if (!listingId || propertiesLoading || !selectedProperty) {
      setNext7DaysSnapshot(null);
      setNext7DaysSnapshotLoading(false);
      setNext7DaysSnapshotError(null);
      if (perfDebug) {
        console.log('[CalendarPage] Snapshot fetch skipped (prerequisites not met)', {
          listingId,
          propertiesLoading,
          hasSelectedProperty: !!selectedProperty,
        });
      }
      return;
    }

    const fetchSnapshot = async () => {
      setNext7DaysSnapshotLoading(true);
      setNext7DaysSnapshotError(null);

      if (perfDebug) {
        console.log('[CalendarPage] Fetching next 7 days snapshot:', {
          listingId,
          listingIdType: typeof listingId,
        });
      }

      try {
        const response = await getNext7DaysSnapshot({
          listingId: listingId,
        });

        // Normalize response payload (handles both wrapped and flat)
        const payload = normalizeApiPayload(response);
        
        if (payload) {
          setNext7DaysSnapshot(payload);
        } else {
          // No valid payload, but don't throw if we already have snapshot
          const errorMsg = response?.message || 'Failed to fetch next 7 days snapshot';
          // Check current snapshot value via ref (preserves original closure behavior)
          if (!next7DaysSnapshotRef.current) {
            throw new Error(errorMsg);
          }
          // Keep existing snapshot (don't update state)
        }
      } catch (err) {
        // PHASE 10: Handle backend validation errors gracefully
        // Backend incorrectly validates reservation_id for listing_id-only endpoints
        const isBackendValidationError = err.message && (
          err.message.includes('reservation_id must be a positive integer') ||
          err.message.includes('INVALID_PARAMETER')
        );
        
        if (isBackendValidationError) {
          // This is a known backend validation bug - request is valid but backend rejects it
          // Silently fall back (no console.error spam)
          const perfDebug = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('perf_debug') === '1';
          if (perfDebug) {
            console.warn('[CalendarPage] Backend validation error (known issue, using fallback):', {
              message: err.message,
              status: err.status,
              errorCode: err.errorCode,
              listingId,
            });
          }
        } else {
          console.error('[CalendarPage] Error fetching next 7 days snapshot:', {
            error: err,
            message: err.message,
            status: err.status,
            errorCode: err.errorCode,
            listingId,
          });
        }
        
        setNext7DaysSnapshotError(err.message || 'Failed to fetch next 7 days snapshot');
        // Don't clear existing snapshot on error (keep last known good state)
        // setNext7DaysSnapshot(null);
      } finally {
        setNext7DaysSnapshotLoading(false);
      }
    };

    fetchSnapshot();
    // Dependencies: Added propertiesLoading and selectedProperty to prevent fetch until prerequisites are met
  }, [listingId, propertiesLoading, selectedProperty]);

  return {
    next7DaysSnapshot,
    next7DaysSnapshotLoading,
    next7DaysSnapshotError,
  };
}
